import { Alert, message } from 'antd'
import { useHistory, type match } from 'react-router-dom'
import { Box } from '@weareberyl/design-system'

import { useSchemeProduct } from '../Detail/data'
import ProductForm, { ProductFormProps } from '../ProductForm'
import { ProductPageHeader } from '../components'
import { prependScheme } from '~/constants/routes'
import {
  SchemeProductDocument,
  useUpdateSchemeProductMutation,
} from '~/gql/generated/graphql'

type Props = {
  match: match<{ scheme_id: string; id: string }>
}

const ProductEdit = ({ match }: Props) => {
  const schemeProductId = match.params.id
  const { loading, data, initialValues } = useSchemeProduct({ schemeProductId })
  const history = useHistory()
  const [updateSchemeProduct, { loading: saving, error }] =
    useUpdateSchemeProductMutation()
  // Display messages from the server if there are errors that get past client side form validation
  const messages = error?.graphQLErrors.map(error => error.message)

  const onFinish: ProductFormProps['onFinish'] = async schemeProductInput => {
    if (!data?.scheme_product) {
      return
    }

    try {
      await updateSchemeProduct({
        variables: {
          schemeProductId,
          schemeProductInput,
        },
        refetchQueries: [SchemeProductDocument],
      })
    } catch {
      message.error('There was an error saving the product')
      return
    }

    history.replace(
      prependScheme(
        `/products/${schemeProductId}`,
        data.scheme_product.scheme_id,
      ),
    )
    message.success('Product updated')
  }

  if (loading) {
    return null
  }

  return (
    <>
      <ProductPageHeader
        id={schemeProductId}
        schemeId={match.params.scheme_id}
        goBack
        isEditing
      />
      {messages && (
        <Box pb={3}>
          <Alert
            message="Couldn't save the product"
            description={messages.map(message => (
              <p key={message}>{message}</p>
            ))}
            type="error"
            showIcon
          />
        </Box>
      )}
      <ProductForm
        initialValues={initialValues}
        loading={saving || loading}
        onFinish={onFinish}
      />
    </>
  )
}

export default ProductEdit
